import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, User } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, SUBCATEGORIES, SUBCATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";

// Two-deck masthead, set the way a magazine sets its cover: a tall, quiet row
// holding nothing but the serif wordmark, then a lightly tracked section deck
// centred beneath it. Utilities sit out at the margins so the centre stays calm.
export function Navbar() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const doSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-50 glass border-b border-line" data-testid="navbar">
            {/* Masthead deck */}
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative flex items-center justify-center min-h-[88px] py-5">
                    <div className="absolute left-0 inset-y-0 flex items-center gap-1">
                        <button
                            className="lg:hidden p-2 rounded-full text-dim hover:text-accent hover:bg-surface-raised transition-colors duration-200"
                            onClick={() => setMobileOpen((v) => !v)}
                            aria-label="Toggle menu"
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                        <button
                            onClick={() => setSearchOpen((v) => !v)}
                            className="p-2 rounded-full text-dim hover:text-accent hover:bg-surface-raised transition-colors duration-200"
                            aria-label="Search"
                            data-testid="navbar-search-toggle"
                        >
                            <Search size={18} />
                        </button>
                    </div>

                    <Link
                        to="/"
                        className="group flex flex-col items-center gap-2.5 px-16 sm:px-24"
                        data-testid="navbar-logo"
                    >
                        <span className="font-display text-[1.6rem] sm:text-[2.1rem] lg:text-[2.75rem] font-semibold leading-[1.05] text-center text-foreground group-hover:text-accent transition-colors duration-300">
                            {brand.site_name}
                        </span>
                        <span className="block h-px w-14 bg-accent/70 transition-[width] duration-300 group-hover:w-24" />
                    </Link>

                    <div className="absolute right-0 inset-y-0 flex items-center gap-4">
                        {user ? (
                            <div className="hidden sm:flex items-center gap-4">
                                <Link
                                    to="/account"
                                    data-testid="navbar-account"
                                    className="flex items-center gap-2 text-sm text-dim hover:text-accent transition-colors duration-200"
                                >
                                    <span className="grid place-items-center w-7 h-7 rounded-full bg-accent-soft text-accent">
                                        <User size={13} />
                                    </span>
                                    {user.name?.split(" ")[0]}
                                </Link>
                                <button
                                    onClick={() => { logout(); navigate("/"); }}
                                    data-testid="navbar-logout"
                                    className="text-sm text-dim hover:text-neg transition-colors duration-200"
                                >
                                    Log out
                                </button>
                            </div>
                        ) : (
                            <div className="hidden sm:flex items-center gap-5">
                                <Link
                                    to="/login"
                                    data-testid="navbar-login"
                                    className="text-sm text-dim hover:text-foreground transition-colors duration-200 link-underline"
                                >
                                    Log in
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="navbar-register"
                                    className="rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold shadow-card hover:shadow-glow transition-shadow duration-200"
                                >
                                    Subscribe
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Section deck */}
            <nav className="hidden lg:block border-t border-line">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                    <ul className="flex items-center justify-center gap-6 xl:gap-9 py-3.5">
                        {CATEGORIES.map((cat) => (
                            <li key={cat} className="relative group">
                                <Link
                                    to={`/category/${cat}`}
                                    data-testid={`nav-cat-${cat}`}
                                    className="flex items-center gap-1.5 text-[13px] font-medium uppercase tracking-[0.13em] text-dim hover:text-accent transition-colors duration-200"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    {cat === "personal-finance" && (
                                        <ChevronDown
                                            size={12}
                                            className="transition-transform duration-300 group-hover:rotate-180"
                                        />
                                    )}
                                </Link>
                                {cat === "personal-finance" && (
                                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-60 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 z-50">
                                        <div className="panel p-2 shadow-lift">
                                            {SUBCATEGORIES.map((sub) => (
                                                <Link
                                                    key={sub}
                                                    to={`/category/personal-finance/${sub}`}
                                                    data-testid={`nav-sub-${sub}`}
                                                    className="block px-3.5 py-2.5 rounded-lg text-sm text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                                                >
                                                    {SUBCATEGORY_LABELS[sub]}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                        <li className="flex items-center gap-6 xl:gap-9">
                            <span className="w-1 h-1 rounded-full bg-line-strong" />
                            <Link
                                to="/archive"
                                data-testid="nav-archive"
                                className="text-[13px] font-medium uppercase tracking-[0.13em] text-dim hover:text-accent transition-colors duration-200"
                            >
                                Archive
                            </Link>
                        </li>
                    </ul>
                </div>
            </nav>

            {/* Search panel */}
            {searchOpen && (
                <div className="border-t border-line bg-surface-sunken" data-testid="navbar-search-panel">
                    <form onSubmit={doSearch} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex gap-3">
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-dim pointer-events-none" />
                            <input
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles, markets, authors..."
                                data-testid="navbar-search-input"
                                className="field !pl-10"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="navbar-search-submit"
                            className="rounded-full bg-accent text-accent-foreground px-7 text-sm font-semibold shadow-card hover:shadow-glow transition-shadow duration-200"
                        >
                            Search
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="lg:hidden border-t border-line bg-surface" data-testid="mobile-menu">
                    <ul className="px-5 py-4">
                        {CATEGORIES.map((cat) => (
                            <li key={cat} className="border-b border-line">
                                <Link
                                    to={`/category/${cat}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="block py-3 font-display text-lg font-semibold text-foreground hover:text-accent transition-colors duration-200"
                                >
                                    {CATEGORY_LABELS[cat]}
                                </Link>
                            </li>
                        ))}
                        <li className="border-b border-line">
                            <Link
                                to="/archive"
                                onClick={() => setMobileOpen(false)}
                                className="block py-3 font-display text-lg font-semibold text-dim hover:text-accent transition-colors duration-200"
                            >
                                Archive
                            </Link>
                        </li>
                        {!user && (
                            <li className="flex gap-3 pt-5">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-3 rounded-full border border-line-strong text-sm font-medium text-dim">
                                    Log in
                                </Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-3 rounded-full bg-accent text-accent-foreground text-sm font-semibold">
                                    Subscribe
                                </Link>
                            </li>
                        )}
                        {user && (
                            <li className="pt-5 space-y-3">
                                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="block w-full text-center py-3 rounded-full border border-line-strong text-sm font-medium text-dim">
                                    My account
                                </Link>
                                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full py-3 rounded-full border border-line-strong text-sm font-medium text-neg">
                                    Log out
                                </button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
